"use client";

/**
 * Loads an image, then renders it in the DOM.
 *
 * FIXME:
 * There seems to a problem with Safari where onload event fires a few seconds
 * before the browser actually renders the image. Setting the src to a
 * tiny data URL prevents the browser from placing the image before
 * it actually loads, but the loadState.loaded still doesn't accurately
 * reflect when the image is fully ready to be displayed.
 */

import React, { useState, useEffect, useLayoutEffect, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { centered, getColor } from "../utils";
import Box, { BoxProps } from "../Box/Box";
import Icon from "../Icon/Icon";
import { SuccessCallback } from "../../models";

const imageAnimation = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

type ContainImageProps = {
  animate: boolean;
  onDragStart: React.DragEventHandler<HTMLElement>;
};
const ContainImage = styled.img<ContainImageProps>`
  ${centered}
  max-height: 100%;
  max-width: 100%;
  animation: ${imageAnimation} 0.3s ease;
  animation-duration: ${props => (props.animate ? "0.3s" : "0s")};
`;

type CoverImageProps = {
  src: string;
  alt: string;
} & ContainImageProps;
const CoverImage = styled(Box)<CoverImageProps>`
  ${centered}
  width: 100%;
  height: 100%;
  background: ${props => `url(${props.src})`};
  background-position: center;
  background-size: cover;
  animation: ${imageAnimation} 0.3s ease;
  animation-duration: ${props => (props.animate ? "0.3s" : "0s")};
`;

type Props = {
  id?: string;
  alt?: string;
  src?: string | { src: string; width?: number; height?: number };
  srcFallback?: string;
  cover?: boolean;
  icon?: string;
  iconSize?: string | number;
  iconFill?: string;
  onDragStart?: React.DragEventHandler<HTMLElement>;
  /** Automatically set height with respect to a set width */
  aspectRatio?: number;
  onLoad?: SuccessCallback;
  animate?: boolean;
  /** High priority loading for LCP optimization */
  priority?: "high" | "low" | "auto";
  style?: React.CSSProperties;
} & BoxProps;

// Wrap in styled to support built in props
const ImageView: React.FC<Props> = ({
  id,
  alt,
  src,
  srcFallback,
  onDragStart,
  onLoad,
  icon,
  iconSize,
  iconFill,
  cover,
  animate = true,
  priority = "auto",
  ...otherProps
}) => {
  const [loadedSrc, setLoadedSrc] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const onImageLoad = useCallback(() => {
    setLoadedSrc(src);
    setLoaded(true);
    if (onLoad) onLoad(true);
  }, [src, onLoad]);

  const onImageError = useCallback(() => {
    setLoadedSrc(srcFallback);
    setLoaded(true);
    if (onLoad) onLoad(false);
  }, [srcFallback, onLoad]);

  const onImageFallbackLoad = useCallback(() => {
    setLoadedSrc(srcFallback);
    setLoaded(true);
  }, [srcFallback]);

  useLayoutEffect(() => {
    if (src !== loadedSrc) {
      setLoaded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  useEffect(() => {
    if (!src) {
      if (loadedSrc) {
        onImageFallbackLoad();
      }
      return;
    }

    const img = new Image();
    img.src = src as string;
    img.fetchPriority = priority;

    img.addEventListener("load", onImageLoad);
    img.addEventListener("error", onImageError);

    return () => {
      img.removeEventListener("load", onImageLoad);
      img.removeEventListener("error", onImageError);
    };
  }, [onImageFallbackLoad, onImageError, onImageLoad, src, loadedSrc, srcFallback, priority]);

  function renderIcon() {
    return icon ? (
      <Icon
        id="image-view-icon"
        coverParent
        fill={getColor(iconFill, "grayDark")}
        iconId={icon}
        size={iconSize}
      />
    ) : null;
  }
  function renderThumb() {
    const Comp: React.FC<CoverImageProps | ContainImageProps> = cover ? CoverImage : ContainImage;
    return (
      <Comp
        animate={animate}
        alt={alt || null}
        data-testid="image-view-image"
        src={loadedSrc}
        onDragStart={onDragStart}
      />
    );
  }

  return (
    <Box data-testid={id} flexShrink="0" overflow="hidden" {...otherProps}>
      {loaded && loadedSrc ? renderThumb() : renderIcon()}
    </Box>
  );
};

export default ImageView;
